import { useFeatureFlag } from '@artic/ui/feature-flags';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQueryState } from 'nuqs';

import {
  factoriesListQueryOptions,
  FACTORY_ADMIN_KEY,
  type FactoryListItem,
} from '../../features/factory-admin/queries';
import { usePartnerId } from '../../features/inbox/use-partner-name';
import { PatternEditorView } from '../../features/shift-patterns/pattern-editor';
import { SHIFT_PATTERNS_KEY } from '../../features/shift-patterns/queries';
import { shiftPatternsSearchParsers } from '../../features/shift-patterns/search';
import type { ShiftPattern } from '../../features/shift-patterns/types';

export const Route = createFileRoute('/shift-patterns/$shift_pattern_id')({
  staticData: {
    // Emit up to two crumbs: "{factory} > {pattern}". The factory crumb links
    // back to the list with `?factory=` preserved (TanStack <Link> drops
    // search params unless a `search` prop is passed). Both labels come from
    // caches, so we include only the crumbs that have resolved and return null
    // when neither has — same technique as routes/factories.
    breadcrumb: (match, queryClient) => {
      const crumbs: { label: string; path: string; search?: Record<string, string> }[] = [];

      const factoryId = (match.search as { factory?: string }).factory;
      if (factoryId) {
        const factoryLists = queryClient.getQueriesData<FactoryListItem[]>({
          queryKey: [FACTORY_ADMIN_KEY, 'list'],
        });
        const factory = factoryLists
          .flatMap(([, data]) => data ?? [])
          .find((f) => f.factory_id === factoryId);
        if (factory) {
          crumbs.push({
            label: factory.factory_name ?? '(unnamed)',
            path: '/shift-patterns',
            search: { factory: factoryId },
          });
        }
      }

      const { shift_pattern_id: patternId } = match.params as { shift_pattern_id: string };
      const lists = queryClient.getQueriesData<ShiftPattern[]>({
        queryKey: [SHIFT_PATTERNS_KEY, 'patterns'],
      });
      const pattern = lists
        .flatMap(([, data]) => data ?? [])
        .find((p) => p.shift_pattern_id === patternId);
      if (pattern) {
        crumbs.push({
          label: pattern.pattern_name,
          path: match.pathname,
          ...(factoryId ? { search: { factory: factoryId } } : {}),
        });
      }

      return crumbs.length > 0 ? crumbs : null;
    },
  },
  component: ShiftPatternEditorPage,
});

function ShiftPatternEditorPage() {
  const enabled = useFeatureFlag('capacity_plan');
  const { shift_pattern_id: patternId } = Route.useParams();
  // Mount the factories list query so the factory crumb resolves on a hard
  // reload of the editor URL (the breadcrumbs re-render on query-cache events).
  const partnerId = usePartnerId();
  useQuery(factoriesListQueryOptions(partnerId));
  // The editor URL carries the factory in `?factory=` (set by the list's
  // links) — the patterns list endpoint is factory-scoped.
  const [factoryId] = useQueryState('factory', shiftPatternsSearchParsers.factory);

  if (!enabled) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-20">
        <h2 className="text-lg font-semibold">Feature not available</h2>
        <p className="text-muted-foreground text-sm">
          Shift patterns in Quality are not enabled for your organisation.
        </p>
      </div>
    );
  }

  if (!factoryId) {
    return (
      <div className="text-muted-foreground flex flex-col items-center gap-3 py-20 text-center text-sm">
        This link is missing its factory context.
        <Link to="/shift-patterns" className="underline">
          Back to Shift Patterns
        </Link>
      </div>
    );
  }

  return <PatternEditorView factoryId={factoryId} patternId={patternId} />;
}
